import * as m from "@paraglide/messages"
import { PlaydataSection } from "@playdata/PlaydataPrimitives"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/company")({
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_company_meta_description({}, { locale }),
      locale,
      path: "/playdata/company",
      title: m.playdata_company_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

/**
 * The live page is a two-column definition list. Company details reuse the
 * PLAYBRAIN message keys: the live PLAYDATA page still lists a pre-2023
 * address and a superseded board, and it is the same legal entity.
 */
function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale

  const rows: { label: string; values: string[] }[] = [
    {
      label: m.company_companyDetails_companyNameLabel({}, { locale }),
      values: [
        `${m.company_companyDetails_pbInc({}, { locale })} ${m.company_companyDetails_pbIncJa({}, { locale })}`,
      ],
    },
    {
      label: m.company_companyDetails_addressLabel({}, { locale }),
      values: [
        [
          m.company_companyDetails_address({}, { locale }),
          m.company_companyDetails_addressBuilding({}, { locale }),
          m.company_companyDetails_addressKu({}, { locale }),
          m.company_companyDetails_addressCity({}, { locale }),
          m.company_companyDetails_addressPostalCode({}, { locale }),
        ].join(locale === "ja" ? "" : ", "),
      ],
    },
    {
      label: m.company_companyDetails_incorporationDateLabel({}, { locale }),
      values: [m.company_companyDetails_incorporationDate({}, { locale })],
    },
    {
      label: m.company_companyDetails_capitalLabel({}, { locale }),
      values: [m.company_companyDetails_capital({}, { locale })],
    },
    {
      label: m.company_companyDetails_bodLabel({}, { locale }),
      values: [
        m.company_companyDetails_RepDirector({}, { locale }),
        m.company_companyDetails_directorOne({}, { locale }),
        m.company_companyDetails_directorTwo({}, { locale }),
      ],
    },
    {
      label: m.company_companyDetails_businessLabel({}, { locale }),
      values: [m.company_companyDetails_businessDesc({}, { locale })],
    },
  ]

  return (
    <PlaydataSection>
      <h1 className="mb-[3.2rem] text-center text-[2rem]">
        <strong>{m.playdata_company_heading({}, { locale })}</strong>
      </h1>
      <dl className="mx-auto grid max-w-[760px] grid-cols-[minmax(11rem,auto)_1fr] gap-x-[3.2rem] gap-y-[1.2rem] text-[1.4rem] leading-[1.7]">
        {rows.map((row) => (
          <div
            className="contents"
            key={row.label}
          >
            <dt>
              <strong>{row.label}</strong>
            </dt>
            <dd className="flex flex-col">
              {row.values.map((value) => (
                <span key={value}>{value}</span>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    </PlaydataSection>
  )
}
